/* =========================================================================
 * IBC 2.0 — QUICK ADD
 * -------------------------------------------------------------------------
 * Two behaviours, chosen by Liquid, never by this file:
 *
 *   data-ibc-quick-add-mode="add"     one POST to /cart/add.js
 *   data-ibc-quick-add-mode="dialog"  fetch the product page's quick-add
 *                                     section and show it in a dialog
 *
 * The dialog body is the real product form, fetched through the Section
 * Rendering API from `<product url>?section_id=ibc-quick-add-form`. There is
 * no second variant/add-on implementation here — if the product page can
 * build the line, so can the dialog, because it is the same markup.
 *
 * On every successful add this file dispatches `ibc:cart-changed` on
 * `document`, carrying the whole cart on `detail.cart` and `item_count`,
 * `items` and `total_price` at the top level, which is where the header
 * bubble and the cart drawer read them.
 *
 * Dependencies: assets/ibc-dialog.js (custom element `ibc-dialog`).
 * ====================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'ibc-quick-add-form';
  var DIALOG_ID = 'IbcQuickAddDialog';
  var RESET_MS = 1800;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* --------------------------------------------------------------- cart -- */

  function postAdd(body) {
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw new Error(data.description || data.message || 'Sorry, that could not be added.');
        return data;
      });
    });
  }

  function postAddForm(formData) {
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw new Error(data.description || data.message || 'Sorry, that could not be added.');
        return data;
      });
    });
  }

  function announceCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        var detail = { cart: cart };
        if (cart) {
          detail.item_count = cart.item_count;
          detail.items = cart.items;
          detail.total_price = cart.total_price;
        }
        document.dispatchEvent(new CustomEvent('ibc:cart-changed', { bubbles: true, detail: detail }));
        return cart;
      });
  }

  /* ------------------------------------------------------------- dialog -- */

  function buildDialog() {
    var existing = document.getElementById(DIALOG_ID);
    if (existing) return existing;

    var dialog = document.createElement('ibc-dialog');
    dialog.id = DIALOG_ID;
    dialog.className = 'dialog dialog--center ibc-qadd-dialog';
    dialog.hidden = true;
    dialog.innerHTML =
      '<button class="dialog__scrim" type="button" data-dialog-close aria-label="Close quick add"></button>' +
      '<div class="dialog__panel ibc-qadd-panel" role="dialog" aria-modal="true" aria-label="Quick add" tabindex="-1">' +
      '<button type="button" class="icon-btn ibc-qadd__close" data-dialog-close>' +
      '<span class="visually-hidden">Close</span>' +
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">' +
      '<path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.4" fill="none"></path></svg>' +
      '</button>' +
      '<div class="ibc-qadd__content" data-ibc-qadd-content>' +
      '<p class="ibc-qadd__loading" role="status">Loading&hellip;</p>' +
      '</div>' +
      '</div>';

    document.body.appendChild(dialog);
    return dialog;
  }

  function fillDialog(dialog, html) {
    var slot = dialog.querySelector('[data-ibc-qadd-content]');
    if (!slot) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var section =
      parsed.querySelector('[data-ibc-qadd-root]') ||
      parsed.querySelector('#shopify-section-' + SECTION_ID);

    if (!section) {
      slot.innerHTML =
        '<p class="ibc-qadd__error" role="status">Sorry, quick add is unavailable. Please open the product page.</p>';
      return;
    }

    slot.innerHTML = '';
    slot.appendChild(document.importNode(section, true));

    var panel = dialog.querySelector('.dialog__panel');
    var title = slot.querySelector('[data-ibc-qadd-title]');
    if (panel && title) {
      if (!title.id) title.id = 'ibc-qadd-title';
      panel.setAttribute('aria-labelledby', title.id);
      panel.removeAttribute('aria-label');
    }

    if (window.IbcReveal && typeof window.IbcReveal.init === 'function') {
      window.IbcReveal.init(slot);
    }
  }

  function openDialog(button) {
    var url = button.getAttribute('data-ibc-product-url');
    if (!url) return;

    var dialog = buildDialog();
    var slot = dialog.querySelector('[data-ibc-qadd-content]');
    if (slot) {
      slot.innerHTML = '<p class="ibc-qadd__loading" role="status">Loading&hellip;</p>';
    }
    dialog.show(button);

    var separator = url.indexOf('?') === -1 ? '?' : '&';
    fetch(url + separator + 'section_id=' + SECTION_ID, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed');
        return response.text();
      })
      .then(function (html) {
        fillDialog(dialog, html);
        var focusable = dialog.querySelector(
          '.dialog__panel select, .dialog__panel input:not([type="hidden"]), .dialog__panel button:not([data-dialog-close])'
        );
        if (focusable) focusable.focus();
      })
      .catch(function () {
        if (!slot) return;
        slot.innerHTML =
          '<p class="ibc-qadd__error" role="status">Sorry, quick add is unavailable right now. ' +
          '<a class="tlink" href="' +
          url +
          '">Open the product page</a>.</p>';
      });
  }

  /* --------------------------------------------------------- simple add -- */

  function status(scope, message) {
    var card = scope.closest('.ibc-pcard') || scope.parentNode;
    if (!card) return;
    var live = card.querySelector('[data-ibc-quick-add-status]');
    if (!live) {
      live = document.createElement('p');
      live.className = 'ibc-quick-add__status';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('data-ibc-quick-add-status', '');
      card.appendChild(live);
    }
    live.textContent = message;
  }

  function simpleAdd(button) {
    if (button.hasAttribute('aria-disabled')) return;

    var variantId = Number(button.getAttribute('data-ibc-variant-id'));
    if (!variantId) return;

    var original = button.innerHTML;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-busy');
    button.textContent = 'Adding…';

    postAdd({ items: [{ id: variantId, quantity: 1 }] })
      .then(function () {
        return announceCart();
      })
      .then(function () {
        button.textContent = 'Added';
        window.setTimeout(
          function () {
            button.innerHTML = original;
            button.classList.remove('is-busy');
            button.removeAttribute('aria-disabled');
          },
          reducedMotion() ? 600 : RESET_MS
        );
      })
      .catch(function (error) {
        button.innerHTML = original;
        button.classList.remove('is-busy');
        button.removeAttribute('aria-disabled');
        status(button, error.message || 'Sorry, that could not be added.');
      });
  }

  /* -------------------------------------------------------------- wiring -- */

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-ibc-quick-add]');
    if (!button) return;
    event.preventDefault();

    if (button.getAttribute('data-ibc-quick-add-mode') === 'dialog') {
      openDialog(button);
    } else {
      simpleAdd(button);
    }
  });

  /* The dialog's form is the product page's own form. Intercept it so the
     basket updates in place; without JS it posts natively to /cart/add. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches('[data-ibc-qadd-form]')) return;
    event.preventDefault();

    var submit = form.querySelector('[type="submit"]');
    var original = submit ? submit.innerHTML : '';
    var live = form.querySelector('[data-ibc-qadd-status]');

    if (submit) {
      submit.setAttribute('aria-disabled', 'true');
      submit.textContent = 'Adding…';
    }
    if (live) live.textContent = '';

    postAddForm(new FormData(form))
      .then(function () {
        return announceCart();
      })
      .then(function () {
        if (submit) submit.textContent = 'Added';
        var dialog = document.getElementById(DIALOG_ID);
        window.setTimeout(
          function () {
            if (dialog && typeof dialog.hide === 'function') dialog.hide();
            if (submit) {
              submit.innerHTML = original;
              submit.removeAttribute('aria-disabled');
            }
          },
          reducedMotion() ? 300 : 700
        );
      })
      .catch(function (error) {
        if (submit) {
          submit.innerHTML = original;
          submit.removeAttribute('aria-disabled');
        }
        if (live) live.textContent = error.message || 'Sorry, that could not be added.';
      });
  });
})();
